<script setup lang="ts">
// CookieConsentBanner — bottom strip on stemne.no asking permission for the
// Google Ads conversion tag.
//
// Deliberately not a modal. Nothing on the marketing site is gated on an
// undecided visitor, and a consent wall over a landing page is both hostile
// and a conversion killer — the strip sits above the fold's exit, floats
// over the footer, and stays until the visitor picks one of the two.
//
// Visibility is entirely useCookieConsent's `showBanner`: marketing host AND
// either no decision on file or the visitor reopened it from a footer's
// "Cookie-innstillinger" control. On a tenant subdomain the composable is
// inert, so this component renders nothing there even though it is mounted
// app-wide.

import { computed, onBeforeUnmount, ref, watch } from 'vue'
import StemneButton from '@/components/ui/StemneButton.vue'
import { useCookieConsent } from '@/composables/useCookieConsent'

const { consent, reopened, showBanner, accept, decline } = useCookieConsent()

// A reopened banner asks the same question, so it has to say what the answer
// on file currently is — otherwise the visitor cannot tell whether Godta
// would change anything. Keyed on a decided consent as well as `reopened`: a
// reopen before any decision is just the ordinary first-visit banner.
const currentChoiceKey = computed(() => {
  if (!reopened.value) return null
  if (consent.value === 'granted') return 'sharedCookieConsentBanner.currentGranted'
  if (consent.value === 'denied') return 'sharedCookieConsentBanner.currentDenied'
  return null
})

// Reserve the card's footprint at the foot of the page (#3930 fallout).
//
// The card is position:fixed, so it floats over whatever the page puts at
// the bottom of the viewport. On the signup recovery screen that was the
// "Send på nytt" button — the one control that rescues an operator whose
// verification already failed once — and an undecided visitor is exactly
// who both that screen and this banner are for. Playwright could not click
// through it for 10s; a person meets the same card on first paint.
//
// So the card publishes what it occupies and the app wrapper pads by it.
// Same idiom as MobileNav's --app-bottom-nav-height: MEASURED, not guessed,
// because the text wraps and the buttons drop onto their own row at narrow
// widths, which roughly doubles the footprint. A constant would be wrong on
// one breakpoint or the other, and wrong silently.
const INSET_VAR = '--cookie-consent-inset'
// Mirrors `bottom` in the style block below. Clearing the card takes its
// height AND the gap it floats above — reserve only the height and the last
// 16px of content still sits under it, which hides a button's bottom edge.
// A half-covered button reads as a broken one.
const BOTTOM_OFFSET_PX = 16

const cardEl = ref<HTMLElement | null>(null)
let cardResizeObserver: ResizeObserver | null = null

function clearInset(): void {
  if (typeof document === 'undefined') return
  document.documentElement.style.setProperty(INSET_VAR, '0px')
}

function syncInset(): void {
  if (typeof document === 'undefined') return
  const measured = cardEl.value?.getBoundingClientRect().height ?? 0
  if (measured <= 0) {
    clearInset()
    return
  }
  document.documentElement.style.setProperty(INSET_VAR, `${measured + BOTTOM_OFFSET_PX}px`)
}

// Follow the ELEMENT, not the component. App.vue mounts this component on
// every route and keeps it mounted; it is the inner card that is v-if'd
// away the moment the visitor picks Godta or Avslå. A reset wired to
// onBeforeUnmount alone would therefore never fire in the normal flow, and
// every marketing page would keep a dead gap at its foot for the rest of
// the session.
watch(
  cardEl,
  (el) => {
    cardResizeObserver?.disconnect()
    cardResizeObserver = null
    if (!el) {
      clearInset()
      return
    }
    if (typeof ResizeObserver !== 'undefined') {
      cardResizeObserver = new ResizeObserver(syncInset)
      cardResizeObserver.observe(el)
    }
    syncInset()
  },
  { flush: 'post' },
)

// Focus follows a reopen. The first-visit banner appears unasked and must not
// steal focus from the page, but a reopen is the visitor's own action on a
// footer control: leaving focus on that control at the foot of the page would
// strand keyboard and screen-reader users far from the card they just opened.
// Move focus into the card, and hand it back to whatever held it (normally the
// settings control) once they decide — if that element is still in the DOM.
let focusBeforeReopen: HTMLElement | null = null

watch(
  reopened,
  (isReopened) => {
    if (typeof document === 'undefined') return
    if (isReopened) {
      const active = document.activeElement
      focusBeforeReopen = active instanceof HTMLElement ? active : null
      cardEl.value?.focus()
      return
    }
    const restore = focusBeforeReopen
    focusBeforeReopen = null
    if (restore?.isConnected) restore.focus()
  },
  { flush: 'post' },
)

onBeforeUnmount(() => {
  cardResizeObserver?.disconnect()
  cardResizeObserver = null
  clearInset()
})
</script>

<template>
  <div
    v-if="showBanner"
    ref="cardEl"
    class="cookie-consent"
    role="region"
    tabindex="-1"
    :aria-label="$t('sharedCookieConsentBanner.ariaLabel')"
  >
    <div class="cookie-consent__text">
      <p v-if="currentChoiceKey" class="cookie-consent__current">{{ $t(currentChoiceKey) }}</p>
      <p class="cookie-consent__body">{{ $t('sharedCookieConsentBanner.body') }}</p>
    </div>
    <div class="cookie-consent__actions">
      <StemneButton variant="primary" @click="accept">
        {{ $t('sharedCookieConsentBanner.accept') }}
      </StemneButton>
      <StemneButton variant="quiet" @click="decline">
        {{ $t('sharedCookieConsentBanner.decline') }}
      </StemneButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// Floating inset card rather than an edge-to-edge bar: a full-bleed strip
// would have to be square-cornered, and the design system has no 0 radius.
.cookie-consent {
  position: fixed;
  // Above page chrome, below toasts (StemneToastContainer sits at 9999) so
  // a "takk" confirmation is never hidden behind the banner it replaced.
  z-index: 60;
  bottom: 16px;
  left: 50%;
  transform: translate(-50%, 0);
  width: calc(100% - 32px);
  max-width: 640px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;
  background: var(--bg-card, #fbf8f1);
  border: 1px solid var(--border-stronger, #d4c7b1);
  border-radius: var(--radius-lg, 8px);
  box-shadow: var(--shadow-overlay, 0 4px 16px rgba(28, 25, 21, 0.06));
  animation: cookie-consent-in 160ms ease-out;

  // Programmatic focus target on reopen (tabindex -1). Same moss ring as
  // every other focusable surface, only when the browser shows one.
  &:focus-visible {
    outline: 1.5px solid var(--moss);
    outline-offset: 2px;
  }

  &__text {
    // Wrap before the buttons do — the actions keep their intrinsic width
    // and the copy reflows above them on a phone.
    flex: 1 1 260px;
    font-family: var(--font-sans);
    font-size: var(--fs-body-sm, 15px);
    line-height: 1.5;
    color: var(--ink, #1c1915);
  }

  &__body {
    margin: 0;
  }

  &__current {
    margin: 0 0 var(--space-1, 4px);
    color: var(--fg-muted);
  }

  &__actions {
    display: flex;
    flex: 0 0 auto;
    gap: 8px;
  }
}

@keyframes cookie-consent-in {
  from {
    opacity: 0;
    transform: translate(-50%, 8px);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .cookie-consent {
    animation: none;
  }
}
</style>
